/**
 * src/utils/generatePdfStatement.ts
 * Ultra-Premium Executive PDF Statement Generator for CKAudit
 * Built with jsPDF and jspdf-autotable.
 * Generates an executive-ready, multi-page vector financial report.
 */

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { TransactionItem, UserItem } from '../services/api';

export interface StatementSummaryData {
  totalReceived: number;
  totalPaid: number;
  netDifference: number;
  transactionCount?: number;
}

export interface GeneratePdfOptions {
  transactions: TransactionItem[];
  summary: StatementSummaryData;
  currentUser?: UserItem | null;
  periodLabel?: string;
  filename?: string;
}

/**
 * Format Indian numbering system for currency amounts
 */
function formatAmount(amount: number): string {
  const isNeg = amount < 0;
  const absVal = Math.abs(amount);
  const formatted = absVal.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  return `${isNeg ? '-' : ''}${formatted}`;
}

/**
 * Generates and downloads an authentic, executive Apple-style PDF statement.
 */
export async function generateExecutiveStatementPdf({
  transactions,
  summary,
  currentUser,
  periodLabel = 'All Time',
  filename
}: GeneratePdfOptions): Promise<void> {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
    compress: true
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 14;
  const usableWidth = pageWidth - marginX * 2; // 182mm

  const today = new Date();
  const dateStr = today.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
  const timeStr = today.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit'
  });
  const statementRef = `STMT-${today.getFullYear()}-${Math.random().toString(36).substring(2, 7).toUpperCase()}`;

  // -------------------------------------------------------------
  // 1. Sleek Top Accent Banner (Deep Titanium Slate & Indigo Accent)
  // -------------------------------------------------------------
  // Top deep header block
  doc.setFillColor(15, 23, 42); // slate-900
  doc.rect(0, 0, pageWidth, 28, 'F');

  // Accent gradient hairline
  doc.setFillColor(79, 70, 229); // indigo-600
  doc.rect(0, 27, pageWidth, 1.2, 'F');

  // CKAudit Wordmark & Shield
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(255, 255, 255);
  doc.text('CKAudit', marginX, 12);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(148, 163, 184); // slate-400
  doc.text('SMART CREDIT & DEBIT GROUP LEDGER', marginX, 18);

  // Right-aligned header badge: Statement Document Type
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(255, 255, 255);
  doc.text('EXECUTIVE STATEMENT', pageWidth - marginX, 12, { align: 'right' });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(52, 211, 153); // emerald-400
  doc.text('VERIFIED AUDIT REPORT', pageWidth - marginX, 18, { align: 'right' });

  // -------------------------------------------------------------
  // 2. Cardholder & Statement Metadata Card
  // -------------------------------------------------------------
  const metaY = 34;
  const metaH = 24;

  // Background card
  doc.setFillColor(248, 250, 252); // slate-50
  doc.setDrawColor(226, 232, 240); // slate-200
  doc.setLineWidth(0.3);
  doc.roundedRect(marginX, metaY, usableWidth, metaH, 2.5, 2.5, 'FD');

  // Left column: Account & Cardholder
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(100, 116, 139); // slate-500
  doc.text('CARDHOLDER & ACCOUNT', marginX + 4, metaY + 6);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.setTextColor(15, 23, 42); // slate-900
  const holderName = currentUser?.username || 'Executive Master';
  doc.text(holderName, marginX + 4, metaY + 12);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(100, 116, 139);
  const roleLabel = currentUser?.role === 'admin' ? 'Master Administrator' : 'Ledger Member';
  doc.text(`Role: ${roleLabel}  •  ID: #CK-ACC-${currentUser?.id || '001'}`, marginX + 4, metaY + 18);

  // Center column: Period & Generation
  const col2X = marginX + usableWidth * 0.42;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(100, 116, 139);
  doc.text('STATEMENT TIMEFRAME', col2X, metaY + 6);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(15, 23, 42);
  doc.text(periodLabel, col2X, metaY + 12);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(100, 116, 139);
  doc.text(`Generated: ${dateStr} at ${timeStr}`, col2X, metaY + 18);

  // Right column: Reference & Currency
  const col3X = pageWidth - marginX - 4;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(100, 116, 139);
  doc.text('REFERENCE CODE', col3X, metaY + 6, { align: 'right' });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(79, 70, 229); // indigo-600
  doc.text(statementRef, col3X, metaY + 12, { align: 'right' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(100, 116, 139);
  doc.text('Currency: INR (Rs.)', col3X, metaY + 18, { align: 'right' });

  // -------------------------------------------------------------
  // 3. Executive Financial Overview (3 KPI Summary Cards)
  // -------------------------------------------------------------
  const kpiY = metaY + metaH + 4;
  const kpiH = 22;
  const cardGap = 4;
  const cardW = (usableWidth - cardGap * 2) / 3;

  const totalRec = summary.totalReceived ?? 0;
  const totalPaid = summary.totalPaid ?? 0;
  const netDiff = summary.netDifference ?? (totalRec - totalPaid);

  // Helper to draw a KPI card
  const drawKpiCard = (
    x: number,
    label: string,
    amountStr: string,
    subtext: string,
    accentColor: [number, number, number],
    bgLight: [number, number, number]
  ) => {
    // Background
    doc.setFillColor(...bgLight);
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.3);
    doc.roundedRect(x, kpiY, cardW, kpiH, 2.5, 2.5, 'FD');

    // Left border accent bar
    doc.setFillColor(...accentColor);
    doc.roundedRect(x, kpiY, 2, kpiH, 1, 1, 'F');

    // Label
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(100, 116, 139);
    doc.text(label, x + 6, kpiY + 6);

    // Amount Value
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11.5);
    doc.setTextColor(...accentColor);
    doc.text(amountStr, x + 6, kpiY + 13.5);

    // Subtext
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(148, 163, 184);
    doc.text(subtext, x + 6, kpiY + 18.5);
  };

  // 1. Total Received
  drawKpiCard(
    marginX,
    'TOTAL CREDITS (RECEIVED)',
    `+Rs. ${formatAmount(totalRec)}`,
    `${transactions.filter(t => t.transaction_type === 'receive').length} credits logged`,
    [22, 163, 74], // green-600
    [240, 253, 244] // green-50
  );

  // 2. Total Paid Out
  drawKpiCard(
    marginX + cardW + cardGap,
    'TOTAL DEBITS (PAID OUT)',
    `-Rs. ${formatAmount(totalPaid)}`,
    `${transactions.filter(t => t.transaction_type === 'paid').length} debits logged`,
    [220, 38, 38], // red-600
    [254, 242, 242] // red-50
  );

  // 3. Net Balance
  const isNetPos = netDiff >= 0;
  drawKpiCard(
    marginX + (cardW + cardGap) * 2,
    'NET ENDING POSITION',
    `${isNetPos ? '+' : ''}Rs. ${formatAmount(netDiff)}`,
    isNetPos ? 'Credit surplus' : 'Debit deficit',
    isNetPos ? [22, 163, 74] : [220, 38, 38],
    isNetPos ? [240, 253, 244] : [254, 242, 242]
  );

  // -------------------------------------------------------------
  // 4. Category Breakdown Mini-Strip (Executive Insights)
  // -------------------------------------------------------------
  const catTotals: Record<string, number> = {};
  transactions.forEach(t => {
    const c = t.category_name || 'General';
    catTotals[c] = (catTotals[c] || 0) + (t.amount || 0);
  });
  const topCategories = Object.entries(catTotals)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 4);

  const catStripY = kpiY + kpiH + 4;
  if (topCategories.length > 0) {
    doc.setFillColor(241, 245, 249); // slate-100
    doc.setDrawColor(226, 232, 240);
    doc.roundedRect(marginX, catStripY, usableWidth, 9, 1.5, 1.5, 'FD');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(100, 116, 139);
    doc.text('TOP CATEGORIES:', marginX + 4, catStripY + 6);

    let catX = marginX + 32;
    topCategories.forEach(([catName, amt]) => {
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(51, 65, 85);
      doc.text(`${catName}:`, catX, catStripY + 6);

      const labelW = doc.getTextWidth(`${catName}:`);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(79, 70, 229);
      doc.text(` Rs. ${formatAmount(amt)}`, catX + labelW, catStripY + 6);

      catX += labelW + doc.getTextWidth(` Rs. ${formatAmount(amt)}`) + 7;
    });
  }

  // -------------------------------------------------------------
  // 5. Itemized Transactions Table (jspdf-autotable)
  // -------------------------------------------------------------
  const tableStartY = topCategories.length > 0 ? catStripY + 12 : kpiY + kpiH + 6;

  // Prepare table data
  const tableHeaders = [
    'DATE',
    'DESCRIPTION',
    'CATEGORY',
    'MEMBER',
    'TYPE',
    'AMOUNT (INR)'
  ];

  const tableRows = transactions.map(t => {
    const dateFormatted = t.transaction_date ? t.transaction_date.slice(0, 10) : '—';
    const isCredit = t.transaction_type === 'receive';
    const typeLabel = isCredit ? 'CREDIT' : 'DEBIT';
    const amountFormatted = `${isCredit ? '+' : '-'}Rs. ${formatAmount(t.amount || 0)}`;

    return [
      dateFormatted,
      t.description || '—',
      t.category_name || 'General',
      t.username || 'System',
      typeLabel,
      amountFormatted
    ];
  });

  autoTable(doc, {
    startY: tableStartY,
    margin: { left: marginX, right: marginX, bottom: 18 },
    head: [tableHeaders],
    body: tableRows,
    theme: 'plain',
    styles: {
      font: 'helvetica',
      fontSize: 8,
      cellPadding: 2.6,
      overflow: 'linebreak',
      textColor: [30, 41, 59], // slate-800
      lineColor: [226, 232, 240], // slate-200
      lineWidth: 0.1
    },
    headStyles: {
      fillColor: [15, 23, 42], // slate-900
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 7.5,
      halign: 'left'
    },
    columnStyles: {
      0: { cellWidth: 22, fontStyle: 'normal' }, // Date
      1: { cellWidth: 'auto', fontStyle: 'bold' }, // Description
      2: { cellWidth: 28 }, // Category
      3: { cellWidth: 26 }, // Member
      4: { cellWidth: 20, halign: 'center' }, // Type
      5: { cellWidth: 32, halign: 'right', fontStyle: 'bold' } // Amount
    },
    alternateRowStyles: {
      fillColor: [248, 250, 252] // slate-50 alternating
    },
    didParseCell: (data) => {
      // Color-code Type & Amount columns
      if (data.section === 'body') {
        const rowRaw = tableRows[data.row.index];
        const isCredit = rowRaw && rowRaw[4] === 'CREDIT';

        if (data.column.index === 4) {
          // Type column
          data.cell.styles.textColor = isCredit ? [22, 163, 74] : [220, 38, 38];
          data.cell.styles.fontStyle = 'bold';
        }
        if (data.column.index === 5) {
          // Amount column
          data.cell.styles.textColor = isCredit ? [22, 163, 74] : [220, 38, 38];
          data.cell.styles.fontStyle = 'bold';
        }
      }
    },
    didDrawPage: (data) => {
      // Running header on page 2 onwards
      if (data.pageNumber > 1) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8);
        doc.setTextColor(100, 116, 139);
        doc.text(`CKAudit Executive Statement • Reference: ${statementRef}`, marginX, 10);

        doc.setFont('helvetica', 'normal');
        doc.text(`${dateStr} • Page ${data.pageNumber}`, pageWidth - marginX, 10, { align: 'right' });

        doc.setDrawColor(226, 232, 240);
        doc.setLineWidth(0.2);
        doc.line(marginX, 12, pageWidth - marginX, 12);
      }

      // Standard footer on every page
      const footerY = pageHeight - 8;

      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.2);
      doc.line(marginX, footerY - 4, pageWidth - marginX, footerY - 4);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(148, 163, 184); // slate-400
      doc.text('CONFIDENTIAL • CKAUDIT 2.0 SMART FINANCIAL LEDGER', marginX, footerY);

      doc.text(
        'Digitally Encrypted & Certified Audit Trail',
        pageWidth / 2,
        footerY,
        { align: 'center' }
      );

      doc.setFont('helvetica', 'bold');
      doc.text(
        `Page ${data.pageNumber}`,
        pageWidth - marginX,
        footerY,
        { align: 'right' }
      );
    }
  });

  // Calculate actual total pages and update footers if needed
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    const footerY = pageHeight - 8;
    // Overwrite the page count cleanly
    doc.setFillColor(255, 255, 255);
    doc.rect(pageWidth - marginX - 25, footerY - 3, 25, 4.5, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(100, 116, 139);
    doc.text(`Page ${i} of ${totalPages}`, pageWidth - marginX, footerY, { align: 'right' });
  }

  // Trigger download
  const defaultFilename = `CKAudit_Statement_${today.toISOString().slice(0, 10)}_${currentUser?.username || 'All'}.pdf`;
  doc.save(filename || defaultFilename);
}
